import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { Send, CheckCircle2, AlertCircle, ChevronDown } from 'lucide-react';
export function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    service: '',
    budget: '',
    deliveryTime: '',
    email: '',
    whatsapp: '',
    projectDescription: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const services = ['Website Design', 'Mobile App', 'UI/UX', 'Social Media Management', 'Wordpress Website', 'Ecommerce'];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: 'YOUR_ACCESS_KEY_HERE',
          subject: 'New Project Inquiry from KM Consulting Website',
          from_name: formData.name,
          replyto: formData.email,
          to: 'kmconsulting.it@gmail.com',
          name: formData.name,
          email: formData.email,
          whatsapp: formData.whatsapp,
          service: formData.service,
          budget: formData.budget,
          delivery_time: formData.deliveryTime,
          project_description: formData.projectDescription
        })
      });
      const result = await response.json();
      if (result.success) {
        setStatus('success');
        setFormData({
          name: '',
          service: '',
          budget: '',
          deliveryTime: '',
          email: '',
          whatsapp: '',
          projectDescription: ''
        });
        // Reset success message after 5 seconds
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        throw new Error(result.message || 'Something went wrong');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Failed to send message. Please try again or contact us via WhatsApp.');
    }
  };
  const inputClasses = 'w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-gray-500 focus:border-[#d8a43e] focus:ring-1 focus:ring-[#d8a43e]/50 focus:outline-none transition-all appearance-none';
  const labelClasses = 'block text-sm font-medium text-gray-300 mb-2';
  return <div className="w-full max-w-4xl mx-auto mt-16" data-id="element-32">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="bg-[#0C1525]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-10 shadow-2xl relative overflow-hidden" data-id="element-33">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#d8a43e]/5 rounded-full blur-3xl -z-10" data-id="element-34" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-900/10 rounded-full blur-3xl -z-10" data-id="element-35" />

        <div className="mb-8 text-center md:text-left" data-id="element-36">
          <h3 className="text-2xl font-bold text-white mb-2" data-id="element-37">
            Start Your Project
          </h3>
          <p className="text-gray-400" data-id="element-38">
            Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-39">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-40">
            {/* Name */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} data-id="element-41">
              <label htmlFor="name" className={labelClasses} data-id="element-42">
                Full Name *
              </label>
              <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} placeholder="John Doe" className={inputClasses} data-id="element-43" />
            </motion.div>

            {/* Email */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} data-id="element-44">
              <label htmlFor="email" className={labelClasses} data-id="element-45">
                Email Address *
              </label>
              <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} placeholder="john@example.com" className={inputClasses} data-id="element-46" />
            </motion.div>

            {/* WhatsApp */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} data-id="element-47">
              <label htmlFor="whatsapp" className={labelClasses} data-id="element-48">
                WhatsApp Number *
              </label>
              <input type="tel" id="whatsapp" name="whatsapp" required value={formData.whatsapp} onChange={handleChange} placeholder="+44 7000 000000" className={inputClasses} data-id="element-49" />
            </motion.div>

            {/* Service Selection */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="relative" data-id="element-50">
              <label htmlFor="service" className={labelClasses} data-id="element-51">
                Service Interested In *
              </label>
              <div className="relative" data-id="element-52">
                <select id="service" name="service" required value={formData.service} onChange={handleChange} className={`${inputClasses} cursor-pointer`} data-id="element-53">
                  <option value="" disabled data-id="element-54">
                    Select a service
                  </option>
                  {services.map(service => <option key={service} value={service} className="bg-[#0C1525] text-white" data-id="element-55">
                      {service}
                    </option>)}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-56" />
              </div>
            </motion.div>

            {/* Budget */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} data-id="element-57">
              <label htmlFor="budget" className={labelClasses} data-id="element-58">
                Your Budget
              </label>
              <input type="text" id="budget" name="budget" value={formData.budget} onChange={handleChange} placeholder="e.g. £500 - £2000" className={inputClasses} data-id="element-59" />
            </motion.div>

            {/* Delivery Time */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} data-id="element-60">
              <label htmlFor="deliveryTime" className={labelClasses} data-id="element-61">
                Expected Delivery Time
              </label>
              <input type="text" id="deliveryTime" name="deliveryTime" value={formData.deliveryTime} onChange={handleChange} placeholder="e.g. 2-4 weeks" className={inputClasses} data-id="element-62" />
            </motion.div>
          </div>

          {/* Project Description - Full Width */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.35
        }} data-id="element-63">
            <label htmlFor="projectDescription" className={labelClasses} data-id="element-64">
              Project Description *
            </label>
            <textarea id="projectDescription" name="projectDescription" required value={formData.projectDescription} onChange={handleChange} placeholder="Tell us about your project — what you need, your goals, any specific features or requirements..." rows={4} className={`${inputClasses} resize-none`} data-id="element-65" />
          </motion.div>

          {/* Status Messages */}
          <AnimatePresence mode="wait" data-id="element-66">
            {status === 'success' && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center gap-3 text-green-400" data-id="element-67">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" data-id="element-68" />
                <p className="text-sm" data-id="element-69">
                  Thank you! Your message has been sent successfully. We'll be
                  in touch soon.
                </p>
              </motion.div>}

            {status === 'error' && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3 text-red-400" data-id="element-70">
                <AlertCircle className="w-5 h-5 flex-shrink-0" data-id="element-71" />
                <p className="text-sm" data-id="element-72">
                  {errorMessage || 'Something went wrong. Please try again.'}
                </p>
              </motion.div>}
          </AnimatePresence>

          {/* Submit Button */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="pt-2" data-id="element-73">
            <Button type="submit" size="lg" className="w-full md:w-auto min-w-[200px]" isLoading={status === 'submitting'} rightIcon={status !== 'submitting' ? <Send className="w-4 h-4" data-id="element-75" /> : undefined} data-id="element-74">
              {status === 'submitting' ? 'Sending...' : 'Send Message'}
            </Button>
          </motion.div>
        </form>
      </motion.div>
    </div>;
}